import React, { useState } from 'react'
import ReportsApi from 'api/ReportsApi'
import {
    PresetDateRange,
    useActiveMerchant,
    SearchableSelect,
} from 'components'
import { IDateRange } from 'components/PresetDateRange'
import useIsMounted from 'hooks/utils/useIsMounted'
import useFeatureToggle from 'hooks/FeatureToggles/useFeatureToggles'
import { IReportFilter } from '..'

type TOption = {
    id: number
    name: string
    value: number
    label: string
}

const ReportsFilters = ({
    setFilterValues,
    filterValues,
    presetDateRange,
}: {
    setFilterValues: (values: IReportFilter) => void
    filterValues: IReportFilter
    presetDateRange: IDateRange
}): React.ReactElement => {
    const { FILTERS = false } = useFeatureToggle('REPORTS')
    const { ALL_GATEWAY_IDS, ALL_PLATFORMS } = FILTERS
    const [midsearchValue, setMidSearchValue] = useState('')
    const [midSearchLoading, setMidSearchLoading] = useState<boolean>(false)
    const [currencysearchValue, setCurrencySearchValue] = useState('')
    const [currencySearchLoading, setCurrencySearchLoading] = useState<boolean>(
        false
    )
    const [value, setValue] = useState({
        mid_id: 'All Mids',
        currency_id: 'All Currencies',
        date_reference: 'Posted Date',
        gateway_id: 'All Gateway IDs',
        platforms: 'All Platforms',
    })
    const [isLoading, setIsLoading] = useState(false)
    const { isMounted } = useIsMounted()

    const { id: merchantId } = useActiveMerchant()

    const [currenciesList, setCurrenciesList] = React.useState<TOption[]>([
        {
            value: -1,
            id: -1,
            name: 'All Currencies',
            label: 'All Currencies',
        },
    ])
    const [midsList, setMidsList] = React.useState<TOption[]>([
        {
            value: -1,
            id: -1,
            name: 'All Mids',
            label: 'All Mids',
        },
    ])

    const handlePickerChange = (e: any, accessor: string) => {
        setFilterValues({
            ...filterValues,
            [accessor]: e.value,
        })
        setValue({
            ...value,
            [accessor]: e.name,
        })

        if (accessor === 'currency_id' && e.value === -1)
            setCurrencySearchValue('')
        if (accessor === 'mid_id' && e.value === -1) setMidSearchValue('')
    }

    React.useEffect(() => {
        setIsLoading(true)
        if (merchantId && midsearchValue.length > 1) {
            setMidSearchLoading(true)
            ReportsApi.getMidsList(+merchantId, midsearchValue, {
                limit: 999,
                status_id: 2, // { status_id :2 } - Returns only active MIDs.
            })
                .then((data) => {
                    let dataWithUnsetOption = data as TOption[]
                    dataWithUnsetOption.unshift({
                        id: -1,
                        name: 'All MIDs',
                        value: -1,
                        label: 'All MIDs',
                    })
                    if (isMounted) setMidsList(dataWithUnsetOption)
                })
                .finally(() => {
                    setIsLoading(false)
                    setMidSearchLoading(false)
                })
        }
    }, [merchantId, midsearchValue]) // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        setIsLoading(true)
        if (merchantId && currencysearchValue.length > 1) {
            setCurrencySearchLoading(true)
            ReportsApi.getCurrenciesList(999, currencysearchValue)
                .then((data) => {
                    let dataWithUnsetOption = data as TOption[]
                    dataWithUnsetOption.unshift({
                        id: -1,
                        name: 'All Currencies',
                        value: -1,
                        label: 'All Currencies',
                    })
                    if (isMounted) setCurrenciesList(dataWithUnsetOption)
                })
                .finally(() => {
                    setIsLoading(false)
                    setCurrencySearchLoading(false)
                })
        }
    }, [merchantId, currencysearchValue]) // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div id={'reportsReportsFilters'}>
            <div className={'emp-inputs'}>
                <SearchableSelect
                    value={value.mid_id}
                    onValueChange={(e: any) => handlePickerChange(e, 'mid_id')}
                    options={
                        midsearchValue.length > 1 && !isLoading
                            ? midsList ?? []
                            : []
                    }
                    searchValue={midsearchValue}
                    onSearchChange={setMidSearchValue}
                    accessor={'name'}
                    testId="midId"
                    debounceDelay={200}
                    loadingValues={midSearchLoading}
                >
                    {midsearchValue.length < 2 && (
                        <div className={'emp-reports-searchableText'}>
                            Please enter 2 or more characters
                        </div>
                    )}
                </SearchableSelect>
            </div>
            <div className={'emp-inputs'}>
                <SearchableSelect
                    value={value.currency_id}
                    onValueChange={(e) => handlePickerChange(e, 'currency_id')}
                    searchValue={currencysearchValue}
                    options={
                        currencysearchValue.length > 1 && !isLoading
                            ? currenciesList ?? []
                            : []
                    }
                    onSearchChange={setCurrencySearchValue}
                    accessor={'name'}
                    testId="currencyId"
                    debounceDelay={200}
                    loadingValues={currencySearchLoading}
                >
                    {currencysearchValue.length < 2 && (
                        <div className={'emp-reports-searchableText'}>
                            Please enter 2 or more characters
                        </div>
                    )}
                </SearchableSelect>
            </div>
            {ALL_GATEWAY_IDS.enabled && (
                <div className={'emp-inputs'}>
                    <SearchableSelect
                        value={value.gateway_id}
                        onValueChange={(e) =>
                            handlePickerChange(e, 'gateway_id')
                        }
                        options={[
                            {
                                name: 'All Gateway IDs',
                                value: 0,
                                id: 0,
                                label: 'All Gateway IDs',
                            },
                        ]}
                        hideSearch
                        accessor={'name'}
                        testId="gatewayId"
                    />
                </div>
            )}
            {ALL_PLATFORMS.enabled && (
                <div className={'emp-inputs'}>
                    <SearchableSelect
                        value={value.platforms}
                        onValueChange={(e) =>
                            handlePickerChange(e, 'platforms')
                        }
                        options={[
                            {
                                name: 'All Platforms',
                                value: 0,
                                id: 0,
                                label: 'All Platforms',
                            },
                        ]}
                        hideSearch
                        accessor={'name'}
                        testId="platforms"
                    />
                </div>
            )}
            <div className={'emp-inputs'}>
                <SearchableSelect
                    value={value.date_reference}
                    onValueChange={(e) =>
                        handlePickerChange(e, 'date_reference')
                    }
                    options={[
                        {
                            name: 'Posted Date',
                            value: 0,
                            id: 0,
                            label: 'Posted Date',
                        },
                        {
                            name: 'Transaction Date',
                            value: 1,
                            id: 1,
                            label: 'Transaction Date',
                        },
                    ]}
                    accessor={'name'}
                    hideSearch
                    testId="dateRef"
                />
            </div>
            <div>
                <PresetDateRange
                    dateRange={presetDateRange}
                    getDateRange={(date: string) => {
                        setFilterValues({
                            ...filterValues,
                            date_range: date,
                        })
                    }}
                    className={'emp-dateRangePicker'}
                    allowFutureDateSelection={false}
                    testId="reports"
                />
            </div>
        </div>
    )
}

export default ReportsFilters
